import {
    ANALYTICS_CHANNEL, CHANNEL_INFO_BY_CHANNEL, ChannelsHelper, DV360_CHANNEL, FACEBOOK_CHANNEL, GOOGLE_CHANNEL,
    LINKEDIN_CHANNEL, MERCADO_ADS_CHANNEL, SEARCH_CONSOLE_CHANNEL, TIKTOK_CHANNEL,
} from '../channels/channels';
import {accountKey} from './lineWizardDraft';

/**
 * La lista del paso 3 · Cuentas, sin red ni React.
 *
 * Imita el selector de cuentas del Dashbo clásico, que es el que le gusta a Pablo (reunión 29/07):
 * todas las cuentas de la agencia, ya desplegadas, las que usa este cliente ARRIBA y un buscador
 * siempre visible —hay agencias con cientos de cuentas (feedback del 04/08, pestañas 22 y 26)—.
 */

// Search Console no puede ser parte de una línea: `campaigns-configurations/add` la rechaza. Tampoco la
// familia «Rentabilidad del negocio» (Escalafy): alimenta la economía del cliente, no una línea.
const EXCLUDED_CHANNELS = [SEARCH_CONSOLE_CHANNEL];

export function excludedFromLine(channel) {
    return EXCLUDED_CHANNELS.includes(channel) || !ChannelsHelper.canBeInBusinessUnit(channel);
}

// El nombre comercial difiere del label histórico de `channels.js` en estos dos.
const CHANNEL_NAME_OVERRIDES = {[FACEBOOK_CHANNEL]: 'Meta Ads', [TIKTOK_CHANNEL]: 'TikTok Ads'};

export function channelName(channel) {
    return CHANNEL_NAME_OVERRIDES[channel] || CHANNEL_INFO_BY_CHANNEL[channel]?.Spanish || channel;
}

/**
 * Los canales que se pueden conectar o ampliar sin salir del wizard: todos vuelven al paso 3. Mercado Ads
 * aparece sólo donde el backend lo habilita (`channels_allowed`).
 */
export const LINKABLE_CHANNELS = [GOOGLE_CHANNEL, FACEBOOK_CHANNEL, TIKTOK_CHANNEL, LINKEDIN_CHANNEL, MERCADO_ADS_CHANNEL,
    ANALYTICS_CHANNEL];

/** Qué canales pedir a `get-accounts`: los habilitados que tienen conexión y pueden ir a una línea. */
export function channelsToLoad(connections) {
    const withConnection = (connections && connections.channels_with_connection) || [];
    return withConnection.filter(channel => !excludedFromLine(channel) && CHANNEL_INFO_BY_CHANNEL[channel]);
}

/**
 * La salida de cada plataforma al pie de la lista.
 *
 * «Vincular más cuentas» va PRIMERO y sólo donde la plataforma ya está conectada: el problema que
 * marcó Pablo tres veces (29/07, 17/09, 22/09) es que la gente, con Google ya conectado, iba derecho
 * a «Conectar cuenta nueva» creyendo que eso sumaba la cuenta a la línea. «Conectar» queda para las
 * plataformas sin conexión.
 */
export function platformExits(connections) {
    const allowed = (connections && connections.channels_allowed) || [];
    const withConnection = (connections && connections.channels_with_connection) || [];
    const exits = LINKABLE_CHANNELS
        .filter(channel => allowed.includes(channel))
        .map(channel => ({channel, connected: withConnection.includes(channel)}));
    return [...exits.filter(exit => exit.connected), ...exits.filter(exit => !exit.connected)];
}

/**
 * La lista «Todas las cuentas de la agencia» con las salidas de plataforma en su lugar: `[{row} | {exit}]`.
 *
 * Las salidas van al pie, después de las cuentas (ver `platformExits`), salvo la de Google Analytics,
 * que va CON SU GRUPO: después de la última propiedad de GA4 a la vista, o arriba de todo si no hay
 * ninguna. Al pie, detrás de cientos de cuentas y de las otras cuatro salidas, quedaba «medio
 * escondida» (recorrido de fede, 01/10, punto 11), y GA4 no es una cuenta más que se suma: es lo
 * que se le asocia a las otras en el paso 4.
 *
 * `filtered`: la lista viene recortada por el buscador. Ahí «arriba de todo» no es el lugar de su
 * grupo sino el del primer resultado: buscar una cuenta de Google Ads ponía «Conectar GA4» encima de
 * lo que se buscó (review C-rec-fe-linea-04). Si el filtro no deja ninguna propiedad de GA4 a la
 * vista, su salida va con las demás, al pie, en su orden.
 */
export function exitsAroundRows(rows, exits, {filtered = false} = {}) {
    let afterGa4 = 0;
    (rows || []).forEach((row, index) => {
        if (row.channel === ANALYTICS_CHANNEL) afterGa4 = index + 1;
    });
    if (filtered && afterGa4 === 0) {
        return [...(rows || []).map(row => ({row})), ...(exits || []).map(exit => ({exit}))];
    }
    const ga4Exits = (exits || []).filter(exit => exit.channel === ANALYTICS_CHANNEL);
    const otherExits = (exits || []).filter(exit => exit.channel !== ANALYTICS_CHANNEL);
    return [
        ...(rows || []).slice(0, afterGa4).map(row => ({row})),
        ...ga4Exits.map(exit => ({exit})),
        ...(rows || []).slice(afterGa4).map(row => ({row})),
        ...otherExits.map(exit => ({exit})),
    ];
}

// Dónde viene cada credencial en `agency/connections/`: sólo los canales que pueden ir a una línea.
const CREDENTIAL_KEYS = {
    [GOOGLE_CHANNEL]: 'google_credentials',
    [FACEBOOK_CHANNEL]: 'facebook_credentials',
    [TIKTOK_CHANNEL]: 'tiktok_credentials',
    [LINKEDIN_CHANNEL]: 'linkedin_credentials',
    [ANALYTICS_CHANNEL]: 'ga4_credentials',
    [DV360_CHANNEL]: 'dv360_credentials',
    [MERCADO_ADS_CHANNEL]: 'mercado_ads_credentials',
};

/**
 * Los canales con una credencial recién conectada cuyas cuentas todavía no llegaron.
 *
 * El backend responde el OAuth ANTES de que el worker descubra las cuentas: el POST encola
 * `sync_ad_accounts_of_agency` y vuelve, y `get-accounts/<canal>` lee lo que ya está guardado. Al
 * volver de conectar GA4, el paso decía «ya puedes elegir la propiedad» sobre una lista sin ninguna
 * (review C-rec-fe-linea-01). `is_syncing` lo calcula el backend por credencial: sin cuentas, con
 * una sincronización en curso y creada hace menos de 30 minutos. Es lo mismo que mira Conexiones
 * del clásico para su polling.
 */
export function channelsSyncing(connections) {
    if (!connections) return [];
    return Object.keys(CREDENTIAL_KEYS).filter(channel =>
        (connections[CREDENTIAL_KEYS[channel]] || []).some(credential => credential && credential.is_syncing));
}

/**
 * Las cuentas por canal después de un refresco silencioso: lo nuevo, salvo en los canales que esta
 * vez no respondieron, que conservan lo que ya se veía. Un refresco de fondo que falla no puede
 * vaciar una lista que estaba bien.
 */
export function mergeRefreshedAccounts(previous, fresh, failed) {
    const merged = {...(fresh || {})};
    (failed || []).forEach(channel => {
        if (previous && previous[channel]) merged[channel] = previous[channel];
    });
    return merged;
}

export function normalizeForSearch(text) {
    return String(text || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

/**
 * Las filas de la lista: `{client: [...], others: [...]}`.
 *
 * `accountsByChannel` es lo que devolvió `get-accounts/<canal>` (ids EXTERNOS, sin cliente) y
 * `clientAccounts` lo que el cliente ya tiene vinculado (`client_ad_accounts` + `client_stores`). Una
 * cuenta del cliente que la agencia ya no ve por `get-accounts` (credencial caída) igual aparece
 * arriba: es suya, y esconderla haría pensar que se desvinculó.
 */
export function buildAccountRows({accountsByChannel, clientAccounts, query}) {
    const seen = new Set();
    const client = [];
    (clientAccounts || []).forEach(account => {
        if (excludedFromLine(account.channel)) return;
        const row = {channel: account.channel, account_id: String(account.account_id), name: account.name,
                     ofClient: true};
        const key = accountKey(row);
        if (seen.has(key)) return;
        seen.add(key);
        client.push(row);
    });

    const others = [];
    Object.keys(accountsByChannel || {}).forEach(channel => {
        if (excludedFromLine(channel)) return;
        (accountsByChannel[channel] || []).forEach(account => {
            const row = {channel, account_id: String(account.id), name: account.name, ofClient: false};
            const key = accountKey(row);
            if (seen.has(key)) return;
            seen.add(key);
            others.push(row);
        });
    });

    const byChannelThenName = (a, b) => (a.channel.localeCompare(b.channel)) || String(a.name).localeCompare(String(b.name));
    client.sort(byChannelThenName);
    others.sort(byChannelThenName);

    const needle = normalizeForSearch(query);
    const matches = row => !needle
        || normalizeForSearch(row.name).includes(needle)
        || normalizeForSearch(row.account_id).includes(needle)
        || normalizeForSearch(channelName(row.channel)).includes(needle);

    return {client: client.filter(matches), others: others.filter(matches), totalOthers: others.length};
}
